/**
 * One running cycle, as a row.
 *
 * This was a card in a two-column grid, and eight cycles filled a screen. The
 * book is thirty-nine slots: a layout that shows a fifth of it has answered
 * "what is running" with a sample. A row holds the same facts in 32 pixels,
 * and the ones that only matter for the cycle a reader has stopped at — the
 * trailing prices, where it stood in the ranking when it was taken, why it is
 * blocked — open underneath it rather than being carried by every row.
 *
 * Nothing here computes a figure. The percentages a table of this kind usually
 * carries are absent for that reason: the server does not send a PnL rate or a
 * capital share per cycle yet, and a browser that divided two balances would
 * be the second place in this system that decides what a result is.
 */
import { useState } from "react";
import { Button } from "@/design-system/Button";

import { Ban, ChevronRight, History as HistoryIcon } from "lucide-react";
import { Link } from "react-router";

import { InstrumentSymbol } from "@/design-system/InstrumentSymbol";
import { StatusBadge, type BadgeTone } from "@/design-system/StatusBadge";
import { CycleChart } from "@/shared/cycles/CycleChart";
import { delistingText } from "@/shared/cycles/delisting";
import { outlookText } from "@/shared/cycles/outlook";
import { slotQuote } from "@/shared/cycles/quote";
import { reasonText } from "@/shared/text/reasons";
import type { SlotResponse } from "@/shared/api";
import { ABSENT } from "@/shared/metric/metric";
import {
  compareDecimal,
  isNegative,
  isZero,
  money,
  percentOf,
  signedPercent,
  signedMoney,
  toPlaces,
  type Money,
  sharePercent,
} from "@/shared/money/money";

import { CapitalUsed } from "./CapitalUsed";
import { refusalLabel, UNREPORTED } from "@/shared/strategy/score";
import { CYCLE_STATE_LABEL, CYCLE_STATE_TONE, recoveryOf } from "@/shared/strategy/recovery";
import { generationOf } from "@/shared/strategy/pools";
import {
  RETURN_TITLE,
  generationOfVersion,
  logicalPoolOf,
  returnOf,
  versionLabel,
  versionOf,
} from "@/shared/strategy/versions";

import { GridProgress } from "./GridProgress";
import { Recovery } from "./Recovery";
import { RecoveryZones, rungsOf } from "./RecoveryZones";
import { CycleScoreExplanation } from "@/shared/strategy/CycleScoreExplanation";

const STATE: Record<string, { readonly label: string; readonly tone: BadgeTone }> = {
  DRAFT: { label: "Черновик", tone: "muted" },
  VALIDATED: { label: "Проверен", tone: "muted" },
  PAPER: { label: "Бумажный", tone: "muted" },
  LIVE: { label: "Работает", tone: "muted" },
  PAUSED: { label: "На паузе", tone: "warning" },
  EMERGENCY_STOPPED: { label: "Аварийно остановлен", tone: "danger" },
  ARCHIVED: { label: "В архиве", tone: "muted" },
};

const WAITING: Record<string, string> = {
  CAPITAL: "ждёт капитала",
  MARKET: "рынок ничего не предложил",
};

const DROP = 20;

/** The live ladder: ten levels, from the entry to thirty per cent below it. */

const STANDING_HELP =
  "Место и оценка контракта в рейтинге его пула: «вход» — какими они были, когда слот его взял, " +
  "«сейчас» — какими стали в последнем проходе. Прочерк означает отсутствие измерения. " +
  "Экран ничего не меняет в стратегии и правилах выхода.";

function age(opened: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(opened).getTime()) / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${String(days)}д ${String(hours % 24)}ч`;
  }
  return hours > 0 ? `${String(hours)}ч ${String(minutes)}м` : `${String(minutes)}м`;
}

function tone(value: Money): string {
  const shown = toPlaces(value);
  if (isZero(shown)) return "text-text-secondary";
  return isNegative(shown) ? "text-status-negative" : "text-status-positive";
}

function pair(rank: number | null | undefined, score: number | null | undefined): string {
  if (rank === undefined || score === undefined || (rank === null && score === null)) return "—";
  return `#${String(rank ?? "—")} · ${String(score ?? "—")}`;
}

function drift(slot: SlotResponse): number | null {
  const entry = slot.skryptos_score_at_entry;
  const current = slot.current_skryptos_score;
  if (entry === null || entry === undefined || current === null || current === undefined) return null;
  return entry - current;
}

/** A cell that carries a figure. Right-aligned, monospaced, one width per digit. */
const FIGURE = "px-3 text-right font-numeric text-body-main tabular-nums whitespace-nowrap";

export type SortKey =
  | "symbol"
  | "pool"
  | "pnl"
  | "entry"
  | "position"
  | "margin"
  | "return"
  | "used"
  | "score"
  | "rank"
  | "leverage"
  | "share"
  | "age"
  | "status";

export type CycleColumn = {
  readonly key: SortKey;
  readonly label: string;
  readonly align: "left" | "right";
  readonly sortable?: boolean;
  /** A second figure folded into this column, sortable on its own. */
  readonly also?: { readonly key: SortKey; readonly label: string };
  /** The same figures as columns of their own, from xl, where there is room. */
  readonly wide?: readonly CycleColumn[];
};
/**
 * How a cycle's return reads.
 *
 * The result scale, because this is a result: a cycle in profit is green and
 * one under water is red, the same way every other outcome on this product is
 * coloured. Base v1 holds through a drawdown by design, so red here is not an
 * alarm — it is where the cycle stands.
 */
function returnTone(value: string | null | undefined): string {
  if (value == null) return "text-text-muted";
  const shown = toPlaces(value);
  if (isZero(shown)) return "text-text-muted";
  return isNegative(shown) ? "text-status-negative" : "text-status-positive";
}

/**
 * The columns, and which of them a reader may order the book by.
 *
 * Sortable where a reader has a question the order answers — the worst
 * position, the deepest ladder, the oldest cycle. Not sortable where the
 * column is an identifier or a state: sorting by symbol answers nothing an
 * operator asks, and it costs a click to discover that.
 */
export const CYCLE_COLUMNS: readonly CycleColumn[] = [
  // Between lg and xl pairs fold into one column, two lines each, so the book
  // fits its panel without a sideways scroll (owner, 2026-10-15). From xl the
  // panel has room, and a figure squeezed under its neighbour in small type was
  // the hard thing to read (owner, 2026-10-16), so each gets its own column.
  { key: "symbol", label: "Символ · пул", align: "left" },
  {
    key: "pnl",
    label: "PnL",
    align: "right",
    sortable: true,
    also: { key: "return", label: "%" },
    wide: [
      { key: "pnl", label: "PnL", align: "right", sortable: true },
      { key: "return", label: "%", align: "right", sortable: true },
    ],
  },
  {
    key: "entry",
    label: "Цена · позиция",
    align: "right",
    wide: [
      { key: "entry", label: "Цена входа", align: "right" },
      { key: "position", label: "Позиция", align: "right" },
    ],
  },
  {
    key: "margin",
    label: "Маржа · плечо",
    align: "right",
    sortable: true,
    wide: [
      { key: "margin", label: "Маржа", align: "right", sortable: true },
      { key: "leverage", label: "Плечо", align: "right" },
      { key: "share", label: "Доля", align: "left" },
    ],
  },
  {
    key: "score",
    label: "Score · ранг",
    align: "right",
    sortable: true,
    wide: [
      { key: "score", label: "Score", align: "right", sortable: true },
      { key: "rank", label: "Ранг", align: "right" },
    ],
  },
  { key: "age", label: "Время", align: "right", sortable: true },
  { key: "status", label: "Статус", align: "left" },
];

/** Every cell a row can draw, folded and split alike, so the detail spans them all. */
const SPAN = CYCLE_COLUMNS.reduce((total, column) => total + 1 + (column.wide?.length ?? 0), 1);
/** The folded score cell and its two wide parts. */
const SCORE_CELLS = 3;

/** On a card (below lg) each figure says what it is; the header row is gone. */
function CardLabel({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return <span className="text-label-caps text-text-muted lg:hidden">{children}</span>;
}

/** A folded pair: drawn between lg and xl, replaced by its own columns from xl. */
const FOLDED = "xl:hidden";
/** One figure of a pair, in a column of its own. Only from xl, never on a card. */
const WIDE = `hidden xl:table-cell ${FIGURE}`;

const CARD_CELL = "max-lg:flex max-lg:min-w-0 max-lg:flex-col max-lg:items-start max-lg:gap-0.5 max-lg:py-1 max-lg:text-left";

export type CycleSort = { readonly key: SortKey | null; readonly descending: boolean };


/** Today's score, or what the coin scored when it was entered and why the
 *  platform's window no longer ranks it (2026-11-04: most open cycles read
 *  «—» with no word why). */
function currentScore(slot: SlotResponse): string {
  if (slot.current_skryptos_score != null) return String(slot.current_skryptos_score);
  if (slot.outside_ranking != null) return "вне рейтинга";
  return "—";
}

function currentRank(slot: SlotResponse): string {
  if (slot.current_rank != null) return `#${String(slot.current_rank)}`;
  if (slot.skryptos_score_at_entry != null) return `при входе ${String(slot.skryptos_score_at_entry)}`;
  return "—";
}

function standingTitle(slot: SlotResponse): string {
  if (slot.current_skryptos_score != null) return "Оценка монеты в последнем рейтинге";
  if (slot.outside_ranking != null) return `Монета выпала из рейтинга: ${refusalLabel(slot.outside_ranking)}`;
  return "Монета не попала в последний рейтинг — текущая оценка не публикуется";
}

export function CycleRow({
  slot,
  capitalLimit,
  scored = true,
}: {
  readonly slot: SlotResponse;
  readonly capitalLimit: Money | null;
  /** False when no cycle in the book has a score: its three cells are not drawn. */
  readonly scored?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const state = STATE[slot.state] ?? { label: slot.state, tone: "muted" as BadgeTone };
  const scoreDrift = drift(slot);
  const drifted = scoreDrift !== null && scoreDrift >= DROP;
  const recovery = recoveryOf(slot);
  const cycleReturn = returnOf(slot);
  const exactTrailing = slot.evidence?.trailing;
  // The phase and nothing else. Evidence records that a cycle was trailing at
  // some point; a cycle now CLOSING is not trailing, and a badge that outlived
  // its phase told the operator the wrong thing about what happens next. The
  // evidence stays readable in the row's own detail.
  const trailing = slot.cycle_phase === "TRAILING";
  const reconciling = slot.cycle_phase === "RECONCILING";
  const detailId = `cycle-detail-${slot.slot_id}`;

  const quoted = slotQuote(slot);
  const waiting = outlookText(slot.outlook, new Date(), quoted);
  return (
    <>
      <tr
        data-testid={`cycle-card-${slot.slot_id}`}
        className="h-10 border-b border-border-subtle bg-surface-default last:border-b-0 hover:bg-surface-hover max-lg:h-auto max-lg:grid max-lg:grid-cols-[auto_1fr_1fr] max-lg:gap-x-3 max-lg:px-2 max-lg:py-2"
      >
        <td className="w-6 pl-2 max-lg:row-span-4 max-lg:pl-0">
          <Button
            variant="ghost"
            aria-expanded={open}
            aria-controls={detailId}
            aria-label={`Подробности цикла ${slot.slot_id}`}
            data-testid={`cycle-expand-${slot.slot_id}`}
            onClick={() => {
              setOpen((was) => !was);
            }}
            className="size-5 min-h-0 p-0 text-text-muted"
          >
            <ChevronRight
              aria-hidden="true"
              className={`size-3.5 transition-transform duration-150 ${open ? "rotate-90" : ""}`}
            />
          </Button>
        </td>

        <td className="bg-inherit px-3 max-lg:col-span-2 max-lg:pb-1 lg:sticky lg:left-0 lg:z-10">
          <span className="flex flex-col leading-tight">
          {slot.symbol === null ? (
            <span className="text-helper-sm text-text-secondary">
              {slot.waiting != null && WAITING[slot.waiting] !== undefined
                ? WAITING[slot.waiting]
                : "символ не выбран"}
            </span>
          ) : (
            <InstrumentSymbol symbol={slot.symbol} />
          )}

        {/* The pool and the strategy the cycle is actually running, together.
            The migration from BASE v1.3 to v2.0 is rolling: a cycle opened
            under v1.3 keeps its rules until it closes, so for the length of the
            overlap the account is running two strategies at once. A screen that
            showed one version for all of them would be describing money at risk
            under rules it is not under. A legacy pool here is the truth about
            an old cycle, not a fault, and it is never relabelled. */}
        {/* Two lines of text rather than the badges the Циклы roster carries.
            Not a different answer — the same two facts, the same generation
            attribute, the same words — but this table holds nineteen rows at
            32px inside a fixed region, and a pair of badges is 40px before it
            wraps. The density gate measures exactly that, and a roster that
            scrolls inside itself is how every block under it stopped being
            read. The full identity is one click away in the row's own
            detail, and on Циклы it leads the line. */}
            <span className="flex gap-1 whitespace-nowrap font-numeric text-helper-sm text-text-muted">
              <span
                data-generation={generationOf(logicalPoolOf(slot) ?? "UNKNOWN")}
                data-testid={`cycle-pool-${slot.slot_id}`}
              >
                {logicalPoolOf(slot) ?? "UNKNOWN"}
              </span>
              {/* The version only where it differs from the current one: the
                  same «BASE v2.0» under every row said nothing. */}
              {(() => {
                const version = versionOf(slot);
                if (version === null || generationOfVersion(version) === "v2") return null;
                return <>
                  <span aria-hidden="true">·</span>
                  <span data-testid={`cycle-version-${slot.slot_id}`}>{versionLabel(version)}</span>
                </>;
              })()}
            </span>
          </span>
        </td>

        <td className={`${FIGURE} ${CARD_CELL} ${FOLDED}`} title={slot.unrealised}>
          <CardLabel>PnL</CardLabel>
          <span className="flex flex-col leading-tight">
          <span className={tone(slot.unrealised)}>{signedMoney(slot.unrealised)}</span>
        {/* The same PnL as a percentage, beside the money. "+0.45" is a good
            result on a small cycle and a poor one on a large cycle, and the
            reader comparing rows has no way to tell which from the money
            alone.

            Measured against the capital the cycle committed, which is the
            base the strategy itself compares against. Margin would be a
            different base and doubles at two-times leverage. */}
          <span
          className={`text-helper-sm ${returnTone(cycleReturn?.value ?? null)}`}
          data-testid={`cycle-return-${slot.slot_id}`}
          data-return-base={cycleReturn?.base}
          title={
            cycleReturn === null
              ? "Нет позиции или нет наблюдаемой цены — доходность не измерена"
              : RETURN_TITLE[cycleReturn.base]
          }
        >
          {cycleReturn === null ? ABSENT : signedPercent(cycleReturn.value)}
          </span>
          </span>
        </td>
        <td className={`${WIDE} ${tone(slot.unrealised)}`} title={slot.unrealised}>
          {signedMoney(slot.unrealised)}
        </td>
        <td className={`${WIDE} ${returnTone(cycleReturn?.value ?? null)}`}>
          {cycleReturn === null ? ABSENT : signedPercent(cycleReturn.value)}
        </td>
        <td className={`${FIGURE} ${CARD_CELL} ${FOLDED} text-text-secondary`}>
          <CardLabel>Цена · позиция</CardLabel>
          <span className="flex flex-col leading-tight">
            <span>{slot.vwap == null ? "—" : quoted(slot.vwap)}</span>
            <span className="text-helper-sm" title={slot.position_size}>{money(slot.position_size)}</span>
          </span>
        </td>
        <td className={`${WIDE} text-text-secondary`}>{slot.vwap == null ? "—" : quoted(slot.vwap)}</td>
        <td className={`${WIDE} text-text-secondary`} title={slot.position_size}>
          {money(slot.position_size)}
        </td>
        <td className={`${FIGURE} ${CARD_CELL} ${FOLDED} text-text-secondary`} title={slot.margin}>
          <CardLabel>Маржа · плечо</CardLabel>
          <span className="flex flex-col items-end gap-1 leading-tight max-lg:items-start">
            <span>
              {money(slot.margin)}
              {slot.leverage !== undefined && (
                <span className="ml-1 text-text-muted">{`${String(slot.leverage)}×`}</span>
              )}
            </span>

        {/* How far under water, said as the measurement it is.
            This column used to be headed "сетка" and drawn as a rail of ten
            cells reading "3/10", which every reader took for filled orders.
            It was never that: the runtime keeps a drawdown *band* of nought to
            four, and the ten was invented in this file. A position thirty
            percent under its own average therefore showed a bar filled less
            than a third — quietest exactly when it mattered most. The real
            filled-level count lives in the expanded row, where evidence
            carries it. */}
            <Share used={slot.margin} limit={capitalLimit} />
          </span>
        </td>
        <td className={`${WIDE} text-text-secondary`} title={slot.margin}>{money(slot.margin)}</td>
        <td className={`${WIDE} text-text-muted`}>
          {slot.leverage === undefined ? "—" : `${String(slot.leverage)}×`}
        </td>
        <td className="hidden px-3 xl:table-cell">
          <Share used={slot.margin} limit={capitalLimit} />
        </td>

        {scored && <>
        {/* The current standing only. Falling back to the score this cycle
            was chosen at put a figure from another moment — and, since the
            move to Score v2, from another scale — under the heading of what
            the contract scores now. The rank beside it was already honest
            about the same absence, so the two disagreed on the same row. The
            entry score keeps its place in the row's own detail, where it is
            labelled as the entry. */}
        <td
          className={`${FIGURE} ${CARD_CELL} ${FOLDED} text-text-secondary`}
          title={standingTitle(slot)}
        >
          <CardLabel>Score · ранг</CardLabel>
          <span className="flex flex-col leading-tight">
            <span>{currentScore(slot)}</span>
            <span className={`text-helper-sm ${drifted ? "text-status-warning" : "text-text-muted"}`}>
              {currentRank(slot)}
            </span>
          </span>
        </td>
        <td className={`${WIDE} text-text-secondary`} title={standingTitle(slot)}>{currentScore(slot)}</td>
        <td className={`${WIDE} ${drifted ? "text-status-warning" : "text-text-muted"}`} title={standingTitle(slot)}>
          {currentRank(slot)}
        </td>
        </>}
        <td className={`${FIGURE} ${CARD_CELL} text-text-muted`}>
          <CardLabel>Время</CardLabel>
          {slot.opened_at === null ? "—" : age(slot.opened_at)}
        </td>

        <td className="px-3 max-lg:col-span-2 max-lg:py-1">
          <span className="flex flex-wrap items-center gap-1">
            <StatusBadge tone={state.tone} testId={`cycle-state-${slot.slot_id}`}>
              {state.label}
            </StatusBadge>
            {trailing && (
              <StatusBadge tone="warning" shape="outlined">
                TRAILING
              </StatusBadge>
            )}
            {reconciling && (
              <StatusBadge
                tone="warning"
                shape="outlined"
                testId={`cycle-reconciling-${slot.slot_id}`}
              >
                СВЕРКА
              </StatusBadge>
            )}
            {slot.delisting_at != null && (
              // The venue delists this contract: the cycle buys nothing more and
              // is closed two hours before the delivery at the latest.
              <StatusBadge tone="danger" testId={`cycle-delisting-${slot.slot_id}`}>
                {delistingText(slot.delisting_at, new Date())}
              </StatusBadge>
            )}
            {/* What a BASE v2.0 cycle is doing beyond running. Armed, tracking
                and holding are three different amounts of money at risk and
                three different next events, so they are three different words
                rather than one "recovery". Absent while the runtime does not
                report a state: guessing "normal" would be this row reporting
                calm it has no evidence for. */}
            {recovery !== null && recovery.state !== "NORMAL" && (
              <StatusBadge
                tone={CYCLE_STATE_TONE[recovery.state]}
                shape="outlined"
                testId={`cycle-recovery-${slot.slot_id}`}
              >
                {CYCLE_STATE_LABEL[recovery.state]}
              </StatusBadge>
            )}
            {slot.blocked_reason !== null && (
              // The reason travels with the icon. A bare glyph says something
              // is wrong and not what, which leaves the reader opening rows to
              // find out — and to a screen reader it says nothing at all,
              // since colour and shape are the only things carrying it.
              <span
                data-testid={`cycle-blocked-${slot.slot_id}`}
                title={`${reasonText(slot.blocked_reason)} (${slot.blocked_reason})`}
                className="flex min-w-0 items-center gap-1 text-helper-sm text-status-warning"
              >
                <Ban aria-hidden="true" className="size-3 shrink-0" />
                {/* Words are longer than the code was: they wrap, never widen the row. */}
                <span className="max-w-[7rem] whitespace-normal leading-tight">{reasonText(slot.blocked_reason)}</span>
              </span>
            )}
          </span>
          {waiting !== null && (
            // What the cycle waits for, said once beside its state, so the
            // reader does not have to open the row to learn why it is idle.
            <span
              className="mt-0.5 block text-helper-sm leading-tight text-text-secondary"
              data-testid={`cycle-outlook-${slot.slot_id}`}
            >
              {waiting}
            </span>
          )}
        </td>
      </tr>

      {open && (
        <tr id={detailId} data-testid={`cycle-detail-row-${slot.slot_id}`} className="bg-surface-hover max-lg:block">
          <td colSpan={scored ? SPAN : SPAN - SCORE_CELLS} className="px-3 py-3 max-lg:block">
            <div className="mb-3 grid gap-4 sm:grid-cols-2">
              <CycleChart slot={slot} />
              <GridProgress depth={slot.grid_depth} evidence={slot.evidence} slotId={slot.slot_id} />
              {slot.static_grid_status != null && (
                <span className="block text-helper-sm text-text-secondary" data-testid={`static-grid-status-${slot.slot_id}`}>
                  STATIC · {slot.static_grid_status === "COMPLETE" ? "Сверена с Bybit" : "Сверяется с Bybit"}
                </span>
              )}
              <CapitalUsed
                used={slot.margin}
                // The cycle's own commitment where it reported one. The
                // schedule's target slot size is what the *next* cycle may
                // take, and at the slot cap it drifts with equity while a
                // running cycle's commitment does not — so a row measured
                // against it showed 8% of 121 where the cycle had undertaken
                // exactly 100.
                limit={slot.undertaken ?? capitalLimit}
                slotId={slot.slot_id}
              />
            </div>
            {recovery !== null && <Recovery reading={recovery} slotId={slot.slot_id} />}
            {/* Kept apart from the grid above on purpose: that strip is orders
                placed at the venue, this one is prices being watched. */}
            <RecoveryZones rungs={rungsOf(slot)} slotId={slot.slot_id} />
            <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
              <Detail label="Слот" value={slot.slot_id} />
              {slot.symbol !== null && (
                // The instrument's record, not the slot's, and it says so.
                // A cycle records no slot, so what this slot traded before the
                // one it holds cannot be asked at all — a link implying
                // otherwise would be a claim the data cannot meet.
                //
                // In the expansion rather than as a column: it is a way out of
                // this screen, and a navigation icon repeated down fifty rows
                // competes with the figures beside it.
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-label-caps text-text-muted">История</span>
                  <Link
                    to={`/app/slots?view=closed&symbol=${encodeURIComponent(slot.symbol)}`}
                    data-testid={`history-${slot.slot_id}`}
                    title={`Закрытые циклы по ${slot.symbol}, а не по этому слоту`}
                    className="inline-flex w-fit cursor-pointer items-center gap-1.5 rounded-row text-body-main text-action-primary transition-colors duration-150 hover:text-action-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
                  >
                    <HistoryIcon aria-hidden="true" className="size-3.5 shrink-0" />
                    {`Сделки по ${slot.symbol}`}
                  </Link>
                </span>
              )}
              {slot.strategy_revision != null && (
                <Detail
                  label="Revision / Candidate"
                  value={slot.strategy_revision}
                  testId={`cycle-strategy-revision-${slot.slot_id}`}
                />
              )}
              {slot.candidate_sha256 != null && (
                <Detail label="Хэш кандидата" value={slot.candidate_sha256} />
              )}
              {/* What the position has settled while it has been held. Most
                  cycles carry nothing — they live four hours and funding
                  settles every eight — so it appears only where there is
                  something to say. On a position held for days it is not
                  small: the open cycle on ONGUSDT had taken in seventeen
                  dollars against a seventy-four dollar drawdown, and nothing
                  on any screen said so. */}
              {slot.funding != null && !isZero(toPlaces(slot.funding)) && (
                <Detail
                  label="Фандинг"
                  testId={`cycle-funding-${slot.slot_id}`}
                  title="Начислено или списано биржей за время удержания позиции. Положительное — заплатили нам."
                  value={
                    <span
                      className={
                        isNegative(toPlaces(slot.funding))
                          ? "text-status-negative"
                          : "text-status-positive"
                      }
                    >
                      {signedMoney(slot.funding)}
                    </span>
                  }
                />
              )}
              <Detail
                label="Рейтинг"
                testId={`standing-${slot.slot_id}`}
                title={STANDING_HELP}
                value={
                  <span className="flex flex-col">
                    <span>вход {pair(slot.rank_at_entry, slot.skryptos_score_at_entry)}</span>
                    {/* The version that actually chose this instrument. Cycles
                        chosen under skryptos-score-1 and score-2 run side by
                        side while the migration rolls, and the entry score is
                        historical evidence — it is not recomputed, and it is
                        never relabelled with today's version. */}
                    <span
                      className="text-helper-sm text-text-muted"
                      data-testid={`entry-score-version-${slot.slot_id}`}
                    >
                      {slot.score_version_at_entry ?? UNREPORTED}
                    </span>
                    <span
                      className={drifted ? "text-status-warning" : undefined}
                      data-drifted={drifted ? "true" : "false"}
                    >
                      сейчас {pair(slot.current_rank, slot.current_skryptos_score)}
                    </span>
                  </span>
                }
              />
              {trailing && exactTrailing != null && (
                <>
                  <Detail
                    label="Текущая цена"
                    value={
                      exactTrailing.current_price == null
                        ? "—"
                        : quoted(exactTrailing.current_price)
                    }
                  />
                  <Detail
                    label="Пик"
                    value={exactTrailing.peak_price == null ? "—" : quoted(exactTrailing.peak_price)}
                  />
                  <Detail
                    label="Выход по трейлингу"
                    value={
                      exactTrailing.exit_trigger_price == null
                        ? "—"
                        : quoted(exactTrailing.exit_trigger_price)
                    }
                  />
                </>
              )}
              {trailing && slot.evidence == null && (
                <>
                  <Detail
                    label="Пик"
                    value={
                      slot.trailing_peak_price == null ? "—" : quoted(slot.trailing_peak_price)
                    }
                  />
                  <Detail
                    label="Выход по трейлингу"
                    value={
                      slot.trailing_exit_trigger_price == null
                        ? "—"
                        : quoted(slot.trailing_exit_trigger_price)
                    }
                  />
                </>
              )}
              {/* The threshold the trailing switches on at, named for what it
                  is. It is not a take profit: the cycle does not close there,
                  it starts following the price from there, and a reader who
                  took it for an exit would expect money that is not coming. */}
              {slot.activation != null && (
                <Detail
                  label="Активация трейлинга"
                  value={signedPercent(slot.activation)}
                  testId={`cycle-activation-${slot.slot_id}`}
                />
              )}
              {/* What the five per cent is five per cent of. The percentage
                  on its own is the ambiguity that hid the defect: read against
                  the margin it asked for half the money it asks for read
                  against the position, and both readings look identical on a
                  screen. The amount is sent by the service already computed —
                  a target is money, and money is not multiplied here. */}
              {slot.exit_net_floor_roi != null && (
                <Detail
                  label={
                    slot.exit_target_base === "DEPLOYED_ENTRY_NOTIONAL"
                      ? "Цель: NET от позиции"
                      : "Цель: NET от маржи"
                  }
                  value={`${signedPercent(slot.exit_net_floor_roi)}${
                    slot.exit_target_net_profit == null
                      ? ""
                      : ` · ${money(slot.exit_target_net_profit)} USDT`
                  } · ${slot.exit_net_floor_active ? "активна" : "не достигнута"}`}
                  testId={`cycle-exit-floor-${slot.slot_id}`}
                />
              )}
              {slot.exit_net_floor_price != null && (
                <Detail
                  label="Цена NET floor"
                  value={quoted(slot.exit_net_floor_price)}
                  testId={`cycle-exit-floor-price-${slot.slot_id}`}
                />
              )}
              {slot.trailing_trigger_price != null && (
                <Detail
                  label="Обычный trailing trigger"
                  value={quoted(slot.trailing_trigger_price)}
                  testId={`cycle-trailing-trigger-${slot.slot_id}`}
                />
              )}
              {slot.effective_exit_trigger_price != null && (
                <Detail
                  label="Эффективный exit trigger"
                  value={quoted(slot.effective_exit_trigger_price)}
                  testId={`cycle-effective-trigger-${slot.slot_id}`}
                />
              )}
              {slot.blocked_reason !== null && (
                <Detail label="Заблокирован" value={reasonText(slot.blocked_reason)} tone="text-status-warning" />
              )}
            </div>
            <CycleScoreExplanation cycleId={slot.cycle_id ?? null} enabled={open} />
          </td>
        </tr>
      )}
    </>
  );
}

function Detail({
  label,
  value,
  title,
  testId,
  tone: colour = "text-text-primary",
}: {
  readonly label: string;
  readonly value: React.ReactNode;
  readonly title?: string;
  readonly testId?: string;
  readonly tone?: string;
}): React.JSX.Element {
  return (
    <span className="min-w-0">
      <span className="block text-label-caps text-text-muted" title={title}>
        {label}
      </span>
      <span
        data-testid={testId}
        className={`mt-0.5 block font-numeric text-helper-sm tabular-nums ${colour}`}
      >
        {value}
      </span>
    </span>
  );
}


/** How much of what one cycle may commit it has committed. */
function Share({
  used,
  limit,
}: {
  readonly used: Money;
  readonly limit: Money | null;
}): React.JSX.Element {
  if (limit === null || compareDecimal(limit, "0") <= 0) {
    return <span className="font-numeric text-helper-sm text-text-muted">—</span>;
  }
  const share = percentOf(used, limit);
  return (
    <span className="flex items-center gap-1.5">
      <span className="font-numeric text-helper-sm tabular-nums text-text-secondary">{sharePercent(share)}</span>
      <span aria-hidden="true" className="h-1 w-10 overflow-hidden rounded-row bg-surface-selected">
        <span
          className="block h-full bg-capital-positions"
          style={{ width: `${share}%` }}
        />
      </span>
    </span>
  );
}
